/**
 * ActivityRenderer — the single entry point Feed and Lesson both mount.
 *
 * Renders the right variant-aware component for an `Activity` by its `type`,
 * threading the uniform contract (`variant`, `onFinish`, `onTrackAttempt`,
 * `onSkip`) through. This is the PWA analogue of iOS `FeedActivityRenderer`
 * (`FeedNew.tsx`) and the lesson activity host — ONE renderer, two variants.
 */
import MCQ from "./components/MCQ";
import Match from "./components/Match";
import Write from "./components/Write";
import Fill from "./components/Fill";
import type { Activity, ActivityComponentContract } from "./types";

export interface ActivityRendererProps extends ActivityComponentContract {
  activity: Activity;
  /** Listening-time telemetry — forwarded to the Fill video bubble's player. */
  onTrackListeningTime?: (time: number) => void;
}

export default function ActivityRenderer({
  activity,
  variant = "lesson",
  onFinish,
  onTrackAttempt,
  onSkip,
  onTrackListeningTime,
}: ActivityRendererProps) {
  const shared = { variant, onFinish, onTrackAttempt, onSkip } as const;

  switch (activity.type) {
    case "mcq":
      return <MCQ data={activity.data} {...shared} />;
    case "mcq-reverse":
      return <MCQ data={activity.data} isReverse {...shared} />;
    case "match":
      return <Match data={activity.data} {...shared} />;
    case "write":
      return <Write data={activity.data} {...shared} />;
    case "fill":
      return (
        <Fill
          data={activity.data}
          onTrackListeningTime={onTrackListeningTime}
          {...shared}
        />
      );
    default: {
      // Exhaustiveness guard — a new ActivityType must be handled above.
      const _exhaustive: never = activity;
      return _exhaustive;
    }
  }
}
